import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import {
  v2,
  CONTACT_FUNCTIONS,
  FUNCTION_LABELS,
  type ContactFunction,
  type ProfileContactKind,
  type ProfileContactRow,
  type ProfileContactWrite,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { fieldDifferences, readSaveConflict, type SaveConflict } from '@/lib/concurrency';
import { Field } from './FieldUsage';
import { SaveConflictBanner } from './SaveConflictBanner';

/**
 * phase-profile-contact-rows (BUILD item 4) — which field-usage key a row's
 * field maps to, so the ⓘ popover keeps reporting the real template/form
 * consumers under the new model.
 *
 * The usage map is derived server-side and still keyed by the legacy profile
 * columns. A row bound to a region reports through that region's agency-contact
 * key; an all-regions row reports through the general Contact* key. Only the
 * 'general' function carries the binding today — a labelling or CMC row has no
 * declared consumer yet, so it shows no ⓘ rather than a misleading one.
 */
const REGION_USAGE_PREFIX: Readonly<Record<string, string>> = {
  FDA: 'FdaAgencyContact',
  HC: 'HcAgencyContact',
  EMA: 'EmaAgencyContact',
  MHRA: 'MhraAgencyContact',
  PMDA: 'PmdaAgencyContact',
  TGA: 'TgaAgencyContact',
};

function usageColumn(
  kind: ProfileContactKind,
  region: string,
  fn: ContactFunction,
  field: 'Name' | 'Title' | 'Email',
): string | undefined {
  if (fn !== 'general') return undefined;
  if (kind === 'signatories') {
    if (field === 'Email') return undefined;
    return `AuthorizedSignatory${field}`;
  }
  if (region === '') return `Contact${field}`;
  const prefix = REGION_USAGE_PREFIX[region];
  if (prefix === undefined) return undefined;
  const sub = field === 'Name' ? 'contactName' : field === 'Title' ? 'contactTitle' : 'contactEmail';
  return `${prefix}.${sub}`;
}

/**
 * phase-profile-contact-rows — the editable ROW LIST for a Company Profile's
 * regulatory contacts (or signatories).
 *
 * Replaces the fixed one-contact-per-agency-card layout: a profile can now carry
 * as many contacts as the department really has, each tagged with a Function, so
 * a labelling cover letter names the labelling contact and a CMC amendment the
 * CMC contact. Rows are grouped by region with an "All regions" group first —
 * matching the resolution chain, which prefers a region's own row and falls back
 * to the all-regions group.
 */

/** Region options — the canonical codes the API stores, with display labels. */
const REGION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All regions' },
  { value: 'FDA', label: 'FDA' },
  { value: 'HC', label: 'Health Canada' },
  { value: 'EMA', label: 'EMA' },
  { value: 'MHRA', label: 'MHRA' },
  { value: 'PMDA', label: 'PMDA' },
  { value: 'TGA', label: 'TGA' },
  { value: 'Swissmedic', label: 'Swissmedic' },
];

function regionLabel(region: string | null): string {
  if (region === null || region === '') return 'All regions';
  return REGION_OPTIONS.find((o) => o.value === region)?.label ?? region;
}

function functionLabel(row: ProfileContactRow): string {
  if (row.Function === 'other') return row.FunctionOther?.trim() || 'Other';
  return FUNCTION_LABELS[row.Function];
}

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  fontSize: 13,
  background: 'var(--surface)',
  color: 'var(--text)',
};

const btnStyle: CSSProperties = {
  background: 'none',
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '4px 10px',
  fontSize: 12,
  cursor: 'pointer',
  color: 'var(--text-muted)',
};

interface DraftRow {
  Region: string;
  Function: ContactFunction;
  FunctionOther: string;
  Name: string;
  Title: string;
  Email: string;
  Phone: string;
  IsPrimary: boolean;
}

function emptyDraft(): DraftRow {
  return {
    Region: '',
    Function: 'general',
    FunctionOther: '',
    Name: '',
    Title: '',
    Email: '',
    Phone: '',
    IsPrimary: false,
  };
}

function toDraft(row: ProfileContactRow): DraftRow {
  return {
    Region: row.Region ?? '',
    Function: row.Function,
    FunctionOther: row.FunctionOther ?? '',
    Name: row.Name ?? '',
    Title: row.Title ?? '',
    Email: row.Email ?? '',
    Phone: row.Phone ?? '',
    IsPrimary: row.IsPrimary,
  };
}

function toWrite(d: DraftRow, kind: ProfileContactKind): ProfileContactWrite {
  const base: ProfileContactWrite = {
    Region: d.Region === '' ? null : d.Region,
    Function: d.Function,
    FunctionOther: d.Function === 'other' ? d.FunctionOther : null,
    IsPrimary: d.IsPrimary,
    Name: d.Name,
    Title: d.Title,
    Email: d.Email,
  };
  if (kind === 'contacts') base.Phone = d.Phone;
  return base;
}

export interface ProfileContactRowsProps {
  readonly profileId: string;
  readonly kind: ProfileContactKind;
  /** Section heading, e.g. "Regulatory contacts". */
  readonly title: string;
  /** Rendered under the heading to explain the resolution behaviour. */
  readonly hint?: string;
}

export function ProfileContactRows({
  profileId,
  kind,
  title,
  hint,
}: ProfileContactRowsProps): ReactElement {
  const [rows, setRows] = useState<ProfileContactRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftRow>(emptyDraft);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  // Tracker row 14 — the row as it was when editing began (its version and the
  // values sent as the save's baseline), and a refused stale save.
  const [editingRow, setEditingRow] = useState<ProfileContactRow | null>(null);
  const [conflict, setConflict] = useState<SaveConflict | null>(null);
  const what = kind === 'contacts' ? 'contact' : 'signatory';

  const closeEditor = (): void => {
    setAdding(false);
    setEditingId(null);
    setEditingRow(null);
    setConflict(null);
    setDraft(emptyDraft());
  };

  const reload = useCallback(async () => {
    try {
      const data = await v2.companyProfiles.contactRows.list(profileId, kind);
      setRows(data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e, 'Could not load rows.'));
    }
  }, [profileId, kind]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      const body = toWrite(draft, kind);
      if (editingId === null) {
        await v2.companyProfiles.contactRows.create(profileId, kind, body);
      } else {
        // Tracker row 14 — the save carries the row version it was made from
        // (UpdatedAt, or CreatedAt for a row never edited) and what was loaded.
        await v2.companyProfiles.contactRows.update(profileId, kind, editingId, body, {
          expectedUpdatedAt: editingRow?.UpdatedAt ?? editingRow?.CreatedAt ?? '',
          ...(editingRow ? { baseline: toWrite(toDraft(editingRow), kind) as Record<string, unknown> } : {}),
        });
      }
      closeEditor();
      await reload();
    } catch (e) {
      // A stale save is refused: keep the editor open with the edits, say so.
      const refused = readSaveConflict(e);
      if (refused) setConflict(refused);
      else setError(formatApiError(e, 'Could not save the row.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string): Promise<void> => {
    setBusy(true);
    try {
      await v2.companyProfiles.contactRows.remove(profileId, kind, id);
      await reload();
    } catch (e) {
      setError(formatApiError(e, 'Could not remove the row.'));
    } finally {
      setBusy(false);
    }
  };

  const setPrimary = async (id: string): Promise<void> => {
    setBusy(true);
    try {
      await v2.companyProfiles.contactRows.setPrimary(profileId, kind, id);
      await reload();
    } catch (e) {
      setError(formatApiError(e, 'Could not set the primary row.'));
    } finally {
      setBusy(false);
    }
  };

  // Group by region, all-regions first, then in REGION_OPTIONS order.
  const groups = new Map<string, ProfileContactRow[]>();
  for (const r of rows ?? []) {
    const key = r.Region ?? '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }
  const orderedKeys = REGION_OPTIONS.map((o) => o.value).filter((k) => groups.has(k));
  for (const k of groups.keys()) if (!orderedKeys.includes(k)) orderedKeys.push(k);

  const editor = (
    <div
      data-testid={`${kind}-editor`}
      style={{
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: 12,
        marginBottom: 10,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 10,
        alignItems: 'end',
      }}
    >
      <label style={{ fontSize: 12 }}>
        Region
        <select
          value={draft.Region}
          onChange={(e) => setDraft({ ...draft, Region: e.target.value })}
          data-testid={`${kind}-draft-region`}
          style={inputStyle}
        >
          {REGION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label style={{ fontSize: 12 }}>
        Function
        <select
          value={draft.Function}
          onChange={(e) => setDraft({ ...draft, Function: e.target.value as ContactFunction })}
          data-testid={`${kind}-draft-function`}
          style={inputStyle}
        >
          {CONTACT_FUNCTIONS.map((f) => (
            <option key={f} value={f}>
              {FUNCTION_LABELS[f]}
            </option>
          ))}
        </select>
      </label>
      {/* The free-text label appears only when it is relevant. */}
      {draft.Function === 'other' && (
        <label style={{ fontSize: 12 }}>
          Function label
          <input
            value={draft.FunctionOther}
            onChange={(e) => setDraft({ ...draft, FunctionOther: e.target.value })}
            data-testid={`${kind}-draft-function-other`}
            style={inputStyle}
          />
        </label>
      )}
      {/* Name/Title/Email carry the ⓘ field-usage popover (BUILD item 4) — the
          consumers are keyed off this row's region + the general binding. */}
      <Field label="Name" column={usageColumn(kind, draft.Region, draft.Function, 'Name')}>
        <input
          value={draft.Name}
          onChange={(e) => setDraft({ ...draft, Name: e.target.value })}
          data-testid={`${kind}-draft-name`}
          style={inputStyle}
        />
      </Field>
      <Field label="Title" column={usageColumn(kind, draft.Region, draft.Function, 'Title')}>
        <input
          value={draft.Title}
          onChange={(e) => setDraft({ ...draft, Title: e.target.value })}
          data-testid={`${kind}-draft-title`}
          style={inputStyle}
        />
      </Field>
      <Field label="Email" column={usageColumn(kind, draft.Region, draft.Function, 'Email')}>
        <input
          type="email"
          value={draft.Email}
          onChange={(e) => setDraft({ ...draft, Email: e.target.value })}
          data-testid={`${kind}-draft-email`}
          style={inputStyle}
        />
      </Field>
      {kind === 'contacts' && (
        <label style={{ fontSize: 12 }}>
          Phone
          <input
            value={draft.Phone}
            onChange={(e) => setDraft({ ...draft, Phone: e.target.value })}
            data-testid={`${kind}-draft-phone`}
            style={inputStyle}
          />
        </label>
      )}
      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="checkbox"
          checked={draft.IsPrimary}
          onChange={(e) => setDraft({ ...draft, IsPrimary: e.target.checked })}
          data-testid={`${kind}-draft-primary`}
        />
        Primary for this region
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy}
          data-testid={`${kind}-save`}
          style={{ ...btnStyle, color: 'var(--text)', fontWeight: 600 }}
        >
          {editingId === null ? 'Add' : 'Save'}
        </button>
        <button
          type="button"
          onClick={closeEditor}
          data-testid={`${kind}-cancel`}
          style={btnStyle}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <fieldset
      data-testid={`${kind}-section`}
      // phase-draggable-resizable-editor-modal — fieldsets default to
      // `min-inline-size: min-content`, so the rigid contact rows (fixed
      // name/function min-widths + badges/buttons) forced a 694px floor and
      // gave the whole editor form a horizontal scrollbar. Let the fieldset
      // shrink; the rows below wrap instead of overflowing.
      style={{ border: 'none', padding: 0, margin: '0 0 16px 0', minInlineSize: 0 }}
    >
      <legend
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-muted)',
          fontWeight: 700,
          marginBottom: 8,
          padding: 0,
        }}
      >
        {title}
      </legend>
      {hint && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>{hint}</div>
      )}

      {error && (
        <div role="alert" style={{ color: '#b91c1c', fontSize: 13, marginBottom: 8 }}>
          {error}
        </div>
      )}

      {rows === null && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
      )}

      {rows !== null && rows.length === 0 && !adding && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
          None yet — add one below.
        </div>
      )}

      {orderedKeys.map((key) => (
        <div key={key || 'all'} data-testid={`${kind}-group-${key || 'all'}`} style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: 6,
            }}
          >
            {regionLabel(key === '' ? null : key)}
          </div>
          {(groups.get(key) ?? []).map((r) => (
            <div
              key={r.Id}
              data-testid={`${kind}-row-${r.Id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                // Wrap rather than force the modal wider than its window —
                // the fixed name/function widths were the scrollbar's cause.
                flexWrap: 'wrap',
                gap: 10,
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                marginBottom: 6,
                fontSize: 13,
              }}
            >
              <span style={{ minWidth: 150, fontWeight: 600 }}>{r.Name || '—'}</span>
              <span style={{ color: 'var(--text-muted)', minWidth: 140 }}>{functionLabel(r)}</span>
              <span style={{ color: 'var(--text-muted)', flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                {[r.Title, r.Email].filter(Boolean).join(' · ') || '—'}
              </span>
              {r.IsPrimary && (
                <span
                  data-testid={`${kind}-primary-badge-${r.Id}`}
                  style={{
                    background: '#dbeafe',
                    color: '#1e3a8a',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  Primary
                </span>
              )}
              {!r.IsPrimary && (
                <button
                  type="button"
                  onClick={() => void setPrimary(r.Id)}
                  disabled={busy}
                  data-testid={`${kind}-set-primary-${r.Id}`}
                  style={btnStyle}
                >
                  Set primary
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setEditingId(r.Id);
                  setEditingRow(r);
                  setConflict(null);
                  setDraft(toDraft(r));
                  setAdding(true);
                }}
                data-testid={`${kind}-edit-${r.Id}`}
                style={btnStyle}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => void remove(r.Id)}
                disabled={busy}
                data-testid={`${kind}-remove-${r.Id}`}
                style={btnStyle}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ))}

      {adding ? (
        <>
          {/* Tracker row 14 — this row was saved by someone else since editing began. */}
          {conflict && (
            <SaveConflictBanner
              what={`This ${what} was changed by someone else.`}
              differences={fieldDifferences(
                conflict.changedFields,
                (f) => (toWrite(draft, kind) as Record<string, unknown>)[f],
                conflict.current,
              )}
              onReload={() => {
                const current = conflict.current as unknown as ProfileContactRow;
                setEditingRow(current);
                setDraft(toDraft(current));
                setConflict(null);
                void reload();
              }}
              testId={`${kind}-save-conflict`}
            />
          )}
          {editor}
        </>
      ) : (
        <button
          type="button"
          onClick={() => {
            setDraft(emptyDraft());
            setEditingId(null);
            setEditingRow(null);
            setConflict(null);
            setAdding(true);
          }}
          data-testid={`${kind}-add`}
          style={btnStyle}
        >
          + Add {kind === 'contacts' ? 'contact' : 'signatory'}
        </button>
      )}
    </fieldset>
  );
}
